import React, { useEffect, useState } from 'react'
import { Button, Form } from 'react-bootstrap';
import { FaRegEdit } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { GrLocation } from "react-icons/gr";
import { BsEye, BsEyeSlash, BsTelephone } from "react-icons/bs";
import { MdOutlineMailOutline } from "react-icons/md";
import { IoMdTime } from "react-icons/io";
import { RiLogoutCircleRLine } from "react-icons/ri";
import CustomisedModal from '../../components/Modal/CustomisedModal';
import logogetto from '../../assets/svg/logo-dashboard-v1.png'
import { getStoresListing } from '../../api/context/manufacturer';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { changePassword, removeFcmToken } from '../../api/context/auth';

const MyProfile = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const isVendor = user?.userType !== 'ADMIN';

    const [modal, setModal] = useState(false);
    const [store, setStore] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState({
        oldPassword: false,
        newPassword: false
    });

    const togglePasswordVisibility = (fieldName) => {
        setShowPassword(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
    };

    const toggleModal = () => setModal(!modal);
    const editStore = () => navigate('/edit-vendors/' + store.id);
    const logoutUser = () => setModal(!modal);

    const logoutStore = () => {
        const fcmToken = localStorage.getItem('fcmToken');
        if (fcmToken) {
            removeFcmToken({ fcmToken })(() => { });
        }
        localStorage.clear();
        toast.success("Log out successfully");
        navigate("/login");
    };

    const formatTime = (timeString) => {
        const time = new Date(`1970-01-01T${timeString}`);
        const hours = time.getHours().toString().padStart(2, '0');
        const minutes = time.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const formik = useFormik({
        initialValues: {
            oldPassword: "",
            newPassword: "",
        },
        validationSchema: Yup.object().shape({
            oldPassword: Yup.string().required('Required'),
            newPassword: Yup.string().required('Required'),
        }),
        onSubmit: (values) => {
            setLoading(true);
            changePassword(values)((response) => {
                if (response && response.statusCode == 200) {
                    toast.success(response?.message);
                    formik.resetForm();
                } else {
                    toast.error(response?.response?.data?.message);
                }
                setLoading(false);
            });
        },
    });

    const getStoresDetails = () => {
        getStoresListing()((response) => {
            if (response?.statusCode == 200) {
                setStore(response?.data?.[0] || {});
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        formik.setFieldValue(name, value.replace(/^\s+/, ''));
    };

    useEffect(() => {
        if (isVendor) getStoresDetails();
    }, [isVendor]);

    const displayName = isVendor
        ? (store?.storeName || user?.firstName || '—')
        : (user?.firstName || '—');
    const displaySubtitle = isVendor
        ? (store?.ownerName || user?.email || '')
        : (user?.email || '');
    const avatarSrc = isVendor && store?.image ? store.image : logogetto;
    const bank = store?.VendorBankDetails?.[0] || {};

    return (
        <div className="profile-page">
            <div className="profile-header">
                <h4>My profile</h4>
                <p>Manage your account details {isVendor ? 'and store information' : ''}.</p>
            </div>

            <div className="profile-hero-card">
                <div className="profile-hero-avatar">
                    <img src={avatarSrc} alt={displayName} />
                </div>
                <div className="profile-hero-body">
                    <h5>{displayName}</h5>
                    <p>{displaySubtitle}</p>
                </div>
                {isVendor && store?.id && (
                    <Button className="profile-edit-btn" onClick={editStore}>
                        <FaRegEdit /> Edit store
                    </Button>
                )}
            </div>

            {isVendor && store?.id && (
                <>
                    <div className="profile-section">
                        <div className="profile-section-head">
                            <h5>Store details</h5>
                        </div>
                        <div className="profile-section-body">
                            <div className="profile-row">
                                <div className="profile-row-label">
                                    <GrLocation />
                                    <span>Location</span>
                                </div>
                                <div className="profile-row-value">
                                    <div>{[store?.address, store?.city, store?.state].filter(Boolean).join(', ') || '—'}</div>
                                    {store?.landmark && <div className="profile-row-sub">{store?.landmark}</div>}
                                </div>
                            </div>

                            <div className="profile-row">
                                <div className="profile-row-label">
                                    <BsTelephone />
                                    <span>Contact number</span>
                                </div>
                                <div className="profile-row-value">{store?.phoneNumber || '—'}</div>
                            </div>

                            <div className="profile-row">
                                <div className="profile-row-label">
                                    <MdOutlineMailOutline />
                                    <span>Email</span>
                                </div>
                                <div className="profile-row-value">{store?.email || '—'}</div>
                            </div>

                            {store?.StoreTimings?.length > 0 && (
                                <div className="profile-row">
                                    <div className="profile-row-label">
                                        <IoMdTime />
                                        <span>Store timings</span>
                                    </div>
                                    <div className="profile-row-value profile-timings">
                                        {store.StoreTimings.map((t, i) => (
                                            <div key={i} className={`profile-timing ${t.isActive ? '' : 'is-closed'}`}>
                                                <span className="day">{t.day}</span>
                                                <span className="time">
                                                    {t.isActive
                                                        ? `${formatTime(t.openingTime)} – ${formatTime(t.closingTime)}`
                                                        : 'Closed'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="profile-grid-2">
                        <div className="profile-section">
                            <div className="profile-section-head">
                                <h5>Bank details</h5>
                            </div>
                            <div className="profile-section-body">
                                <div className="profile-row-simple">
                                    <label>Card holder</label>
                                    <div>{bank?.accountHolderName || '—'}</div>
                                </div>
                                <div className="profile-row-simple">
                                    <label>Account number</label>
                                    <div>{bank?.accountNumber || '—'}</div>
                                </div>
                            </div>
                        </div>

                        <div className="profile-section">
                            <div className="profile-section-head">
                                <h5>Business details</h5>
                            </div>
                            <div className="profile-section-body">
                                <div className="profile-row-simple">
                                    <label>GST</label>
                                    <div>{bank?.gst || '—'}</div>
                                </div>
                                <div className="profile-row-simple">
                                    <label>PAN</label>
                                    <div>{bank?.pan || '—'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className="profile-section">
                <div className="profile-section-head">
                    <h5>Change password</h5>
                    <p>Update the password used to sign in to your account.</p>
                </div>
                <Form onSubmit={formik.handleSubmit} className="profile-section-body">
                    <div className="profile-form-row">
                        <Form.Group>
                            <Form.Label>Old password</Form.Label>
                            <div className="profile-password-input">
                                <Form.Control
                                    type={showPassword.oldPassword ? 'text' : 'password'}
                                    name="oldPassword"
                                    value={formik.values?.oldPassword}
                                    onChange={handleInputChange}
                                    isInvalid={formik.touched.oldPassword && !!formik.errors.oldPassword}
                                    placeholder="Enter old password"
                                />
                                <button
                                    type="button"
                                    className="profile-password-toggle"
                                    onClick={() => togglePasswordVisibility('oldPassword')}
                                    aria-label={showPassword.oldPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword.oldPassword ? <BsEye /> : <BsEyeSlash />}
                                </button>
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.oldPassword}
                                </Form.Control.Feedback>
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>New password</Form.Label>
                            <div className="profile-password-input">
                                <Form.Control
                                    type={showPassword.newPassword ? 'text' : 'password'}
                                    name="newPassword"
                                    value={formik.values?.newPassword}
                                    onChange={handleInputChange}
                                    isInvalid={formik.touched.newPassword && !!formik.errors.newPassword}
                                    placeholder="Enter new password"
                                />
                                <button
                                    type="button"
                                    className="profile-password-toggle"
                                    onClick={() => togglePasswordVisibility('newPassword')}
                                    aria-label={showPassword.newPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword.newPassword ? <BsEye /> : <BsEyeSlash />}
                                </button>
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.newPassword}
                                </Form.Control.Feedback>
                            </div>
                        </Form.Group>
                    </div>

                    <Button className="profile-primary-btn" type="submit" disabled={loading}>
                        {loading ? "Updating..." : "Update password"}
                    </Button>
                </Form>
            </div>

            <div className="profile-logout-row">
                <button type="button" className="profile-logout-btn" onClick={logoutUser}>
                    <RiLogoutCircleRLine /> Log out
                </button>
            </div>

            <CustomisedModal
                isOpen={modal}
                customClass="policy_modal"
                toggle={toggleModal}
            >
                <h5>Are you sure you want to log out?</h5>
                <div className="policy_Agree">
                    <Button onClick={toggleModal} size="lg" className="btn_cancel">Cancel</Button>
                    <Button size="lg" className="btn" onClick={logoutStore}>Log Out</Button>
                </div>
            </CustomisedModal>
        </div>
    );
};

export default MyProfile;
